import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
} from '@/components';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import { getToken, removeToken, isTokenExpired } from '@/utils/auth';


const isDev =
  process.env.NODE_ENV === 'development' || process.env.CI;
const loginPath = '/user/login';

const isProd = process.env.NODE_ENV === 'production';
/*
export const request: RequestConfig = {
  baseURL: isProd ? 'https://your-backend.com/api' : '/api',
  // ... 其他配置
};
*/

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置

export const request: RequestConfig = {
  // 基础 URL（可选，如果代理配置正确，可以不设置）
  baseURL: 'https://proapi.azurewebsites.net',
  ...errorConfig,
};
 */


// 请求拦截器配置
//https://umijs.org/docs/max/request#%E8%BF%90%E8%A1%8C%E6%97%B6%E9%85%8D%E7%BD%AE%E7%A4%BA%E4%BE%8B
export const request: RequestConfig = {
    // 统一的请求设定
  timeout: 60000,
  baseURL: isProd ?  'https://api.tranquillab.app/admin':'',

  //baseURL: 'http://localhost:28000',

  // 请求前拦截：添加 Token
  requestInterceptors: [
    (url: string, options: any) => {


      const token = getToken();
      if (token && !isTokenExpired()) {
        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${token}`,
        };
      }
      // 可选：如果 token 已过期，可以提前跳转登录
       /*if (token && isTokenExpired()) {
          removeToken();
         window.location.href = '/user/login';
         return Promise.reject(new Error('Token expired'));
       }*/

      /*// 从 localStorage 获取 token
      const token = localStorage.getItem('token');
      if (token) {
        // 在请求头中添加 Authorization
        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${token}`,
        };
      }*/

      // 可以在这里添加其他全局请求头，如语言
      options.headers = {
        ...options.headers,
        'Content-Type': 'application/json',
      };

      return { url, options };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    async (response) => {
      // 注意：response 是 umi-request 包装后的对象，数据在 response.data 中
      const data = response.data;

      if (response.status === 401) {
        // 清除本地 token
        removeToken();

        // 跳转到登录页（如果当前不在登录页）
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/user/login')) {
          window.location.href = '/user/login';
        }
      }

      // 检查后端返回的数据结构
      if (data && typeof data === 'object' && 'code' in data) {
        if (data.code === 0) {
          // 将解包后的数据挂载到 response.data 上，后续业务代码拿到的是解包后的数据
          response.data = data.data;
        } else {
          // 业务错误，抛出异常，由 errorHandler 处理
          throw new Error(data.msg || '请求失败');
        }
      }

      // 必须返回 response
      return response;
    },
  ],



  /*  // 响应拦截器
    responseInterceptors: [
      (response) => {
        // 拦截响应数据，进行个性化处理
        const { data } = response;
        if(!data.success){
          message.error('请求失败！');
        }
        return response;
      }
    ]*/


  // 错误处理
  errorConfig: {
    errorHandler: (error: any) => {
      const { response, data } = error;

      // 如果是网络错误（无响应）
      if (!response) {
        // 可以调用 antd 的 message 提示
        // message.error('网络连接异常，请检查网络后重试');
        console.error('网络错误:', error);
        return;
      }

      // 根据状态码处理错误
      const status = response.status;
      switch (status) {
        case 401:
          // 已在响应拦截器中处理，这里可以忽略

        // 跳转到登录页，避免死循环
          //localStorage.removeItem('token');
          removeToken();

          if (typeof window !== 'undefined' && !window.location.pathname.includes('/user/login')) {
            window.location.href = '/user/login';
          }


          break;
        case 403:
          // message.error('没有权限访问');
          break;
        case 404:
          // message.error('请求的资源不存在');
          break;
        case 500:
          // message.error('服务器内部错误，请稍后重试');
          break;
        default:
          // 使用后端返回的错误信息（如果有）
          const errorMsg = data?.message || `请求失败 (${status})`;
          // message.error(errorMsg);
          break;
      }
    },
  },



  //...errorConfig,
  // 错误处理
  /*errorConfig: {
    errorHandler: (error: any) => {
      console.error('请求错误:', error);
    },
  },*/
};
